<template>
  <div class="sci-page-wrapper" style="font-family: 'Inter', sans-serif; background: #f4f6f9; min-height: 100vh;">
    <div class="max-w-5xl mx-auto px-4 pt-24 pb-6">

      <!-- VOLTAR -->
      <button @click="$router.back()" class="flex items-center gap-2 text-slate-500 hover:text-orange-500 transition-colors mb-6 group">
        <v-icon small color="inherit" class="group-hover:-translate-x-1 transition-transform">mdi-arrow-left</v-icon>
        <span class="text-[10px] font-bold uppercase tracking-widest">Voltar para listagem</span>
      </button>

      <v-form ref="form" v-model="valid" lazy-validation>
        <div class="space-y-6 pb-24">

          <!-- SEÇÃO 1: IDENTIFICAÇÃO E ACESSO -->
          <section class="bg-white rounded-xl overflow-hidden shadow-sm border border-slate-100">
            <div class="px-6 py-3 flex items-center gap-3" style="background-color: #333333;">
              <v-icon color="orange" small>mdi-account-circle-outline</v-icon>
              <h2 class="text-white font-bold uppercase tracking-widest text-[11px]">IDENTIFICAÇÃO E ACESSO</h2>
            </div>

            <div class="p-6">
              <div class="flex flex-col md:flex-row gap-8 items-center md:items-start">
                <!-- FOTO -->
                <div class="flex flex-col items-center gap-2 shrink-0">
                  <div
                    class="w-36 h-36 rounded-lg border-2 border-slate-200 bg-slate-100 overflow-hidden cursor-pointer hover:border-orange-400 transition-colors flex items-center justify-center"
                    @click="$refs.fileInput.click()"
                  >
                    <img
                      v-if="data.user_photo"
                      :src="'https://api.sigfrotagov.com.br/uploads/' + data.user_photo"
                      class="w-full h-full object-cover"
                    />
                    <v-icon v-else color="#cbd5e0" size="48">mdi-account</v-icon>
                  </div>
                  <input type="file" accept="image/*" @change="uploadImage" ref="fileInput" style="display:none" />
                  <span class="text-[9px] font-bold text-slate-400 uppercase tracking-widest cursor-pointer hover:text-orange-500 transition-colors" @click="$refs.fileInput.click()">CLIQUE PARA ALTERAR A FOTO</span>
                </div>

                <!-- CAMPOS BÁSICOS -->
                <div class="flex-1 w-full">
                  <v-row dense>
                    <v-col cols="12" md="6">
                      <label class="field-label">LOGIN / USUÁRIO</label>
                      <v-text-field v-model="data.user_login" placeholder="DIGITE O LOGIN" outlined dense hide-details class="field-input font-bold" :rules="fieldRules" />
                    </v-col>
                    
                    <v-col cols="12" md="6">
                      <label class="field-label">SENHA</label>
                      <v-text-field
                        v-model="data.user_password"
                        :type="showPassword ? 'text' : 'password'"
                        placeholder="••••••••••••"
                        outlined dense hide-details
                        class="field-input font-bold"
                        :append-icon="showPassword ? 'mdi-eye-off-outline' : 'mdi-eye-outline'"
                        @click:append="showPassword = !showPassword"
                      />
                    </v-col>

                    <v-col cols="12">
                      <label class="field-label">GRUPO DE ACESSO</label>
                      <v-select
                        v-model="data.group_id"
                        :items="groups"
                        item-text="group_name"
                        item-value="group_id"
                        @change="getTable(data.group_id)"
                        placeholder="SELECIONE O GRUPO"
                        outlined dense hide-details
                        class="field-input font-bold uppercase"
                        :rules="fieldRules"
                      />
                    </v-col>

                    <v-col cols="12">
                      <label class="field-label">E-MAIL CORPORATIVO</label>
                      <v-text-field v-model="data.user_email" type="email" placeholder="EX: USUARIO@EMAIL.COM" outlined dense hide-details class="field-input lowercase" />
                    </v-col>
                  </v-row>
                </div>
              </div>
            </div>
          </section>

          <!-- SEÇÃO 2: VÍNCULO COM ENTIDADE -->
          <section class="bg-white rounded-xl overflow-hidden shadow-sm border border-slate-100">
            <div class="px-6 py-3 flex items-center gap-3" style="background-color: #333333;">
              <v-icon color="orange" small>mdi-link-variant</v-icon>
              <h2 class="text-white font-bold uppercase tracking-widest text-[11px]">VÍNCULO COM ENTIDADE</h2>
            </div>

            <div class="p-6">
              <v-row dense>
                <v-col cols="12">
                  <label class="field-label">VINCULAR A:</label>
                  
                  <div v-if="data.group_id === 1">
                      <v-autocomplete
                        v-model="data.prefectures"
                        :items="table"
                        :item-text="getEntityName"
                        :item-value="getEntityId"
                        multiple
                        chips
                        deletable-chips
                        placeholder="SELECIONE OS MUNICÍPIOS"
                        outlined dense hide-details
                        class="field-input"
                        no-data-text="Nenhum município disponível"
                      >
                        <template v-slot:selection="{ item, index }">
                          <v-chip v-if="index < 3" x-small class="bg-slate-800 text-white font-bold text-[9px] uppercase tracking-widest mt-1 mb-1">
                            <span>{{ getEntityName(item) }}</span>
                          </v-chip>
                          <span v-if="index === 3" class="text-slate-400 text-[9px] font-black ml-2">+{{ data.prefectures.length - 3 }}</span>
                        </template>
                      </v-autocomplete>
                  </div>
                  
                  <v-autocomplete
                    v-else
                    v-model="data.table_id"
                    :items="table"
                    :item-text="getEntityName"
                    :item-value="getEntityId"
                    :disabled="!table || table.length === 0"
                    placeholder="SELECIONE A ENTIDADE"
                    outlined dense hide-details
                    class="field-input font-bold uppercase"
                    no-data-text="Selecione um grupo primeiro"
                  />
                  
                  <p class="text-[9px] text-slate-400 font-bold uppercase tracking-widest mt-4 flex items-center gap-2">
                    <v-icon x-small color="slate-300">mdi-information-outline</v-icon>
                    As opções de vínculo são carregadas com base no grupo de acesso selecionado.
                  </p>
                </v-col>
              </v-row>
            </div>

            <!-- FOOTER AÇÕES -->
            <div class="bg-slate-50 px-6 py-4 border-t border-slate-100 flex justify-end items-center gap-4">
              <button
                type="button"
                @click="$router.back()"
                style="background: none; border: none; cursor: pointer; white-space: nowrap;"
                class="text-slate-400 hover:text-slate-600 font-bold text-[11px] uppercase tracking-widest transition-colors"
              >
                CANCELAR
              </button>
              <button
                type="button"
                @click="validate"
                :disabled="loader"
                style="background-color: #f37021; color: white; display: inline-flex; align-items: center; gap: 8px; border-radius: 8px; padding: 0 24px; font-size: 11px; font-weight: 700; letter-spacing: 0.1em; text-transform: uppercase; border: none; cursor: pointer; height: 42px; width: auto; flex-shrink: 0;"
              >
                <v-icon v-if="loader" small class="animate-spin" color="white">mdi-loading</v-icon>
                <v-icon v-else small color="white">mdi-check</v-icon>
                <span>{{ id ? 'SALVAR ALTERAÇÕES' : 'CONFIRMAR CADASTRO' }}</span>
              </button>
            </div>
          </section>
        </div>
      </v-form>
    </div>

    <!-- Snackbar -->
    <v-snackbar
      v-model="snack.show"
      :color="snack.color"
      timeout="4000"
      top
      elevation="24"
      class="custom-snack"
    >
      <div class="flex items-center gap-3">
        <v-icon color="white" small>{{ snack.color === 'success' ? 'mdi-check-circle' : 'mdi-alert-circle' }}</v-icon>
        <span class="text-xs font-bold uppercase tracking-widest">{{ snack.message }}</span>
      </div>
      <template v-slot:action="{ attrs }">
        <v-btn dark icon v-bind="attrs" @click="snack.show = false">
          <v-icon small>mdi-close</v-icon>
        </v-btn>
      </template>
    </v-snackbar>
  </div>
</template>

<script>
export default {
  props: ["id"],
  data: () => ({
    valid: false,
    loader: false,
    showPassword: false,
    groups: [],
    table: [],
    fieldRules: [(v) => !!v || "Este campo é requerido"],
    snack: {
      show: false,
      message: "",
      color: "success",
    },
    data: {
      user_login: "",
      user_password: null,
      user_photo: "",
      user_email: "",
      group_id: null,
      table_id: null,
      table_key: "",
      prefectures: [],
    },
  }),
  async created() {
    const groupsRes = await this.$http.get("/access-groups");
    this.groups = groupsRes.data;

    if (this.id) {
      this.$store.dispatch("setLoader", true);
      this.$http.get(`users/${this.id}`).then((r) => {
        this.data = r.data[0];
        if (!this.data.prefectures) {
          this.$set(this.data, 'prefectures', []);
        }
        this.getTable(this.data.group_id);
        delete this.data.created_at;
        this.$store.dispatch("setLoader", false);
      });
    }
  },
  methods: {
    getEntityId(item) {
      if (this.data.table_key === 'providers') return item.provider_id;
      if (this.data.table_key === 'prefectures') return item.prefecture_id;
      if (this.data.table_key === 'drivers') return item.driver_id;
      if (this.data.table_key === 'attendants') return item.attendant_id;
      return '';
    },
    getEntityName(item) {
      if (this.data.table_key === 'providers') return item.provider_name;
      if (this.data.table_key === 'prefectures') return item.prefecture_social_name;
      if (this.data.table_key === 'drivers') return item.driver_name;
      if (this.data.table_key === 'attendants') return item.attendant_name;
      return '';
    },
    getTable(gid) {
      if (gid === 1) {
        this.data.table_key = 'prefectures';
        this.$http.get('/prefectures')
          .then((r) => (this.table = r.data))
          .catch(() => (this.table = []));
        return;
      }
      
      const tb = this.groups.filter((v) => v.group_id == gid);
      this.table = [];
      
      if (!tb || tb.length === 0) return;

      setTimeout(() => {
        const modules = this.$store.getters.getModules;
        const vl = modules.filter(
          (nv) => nv.nome.toLowerCase() == tb[0].group_name.toLowerCase()
        );
        if (vl && vl.length > 0) {
          this.data.table_key = vl[0].name;
          this.$http(`/${vl[0].name}`)
            .then((r) => (this.table = r.data))
            .catch(() => (this.table = []));
        } else {
          this.table = [];
        }
      }, 200);
    },
    validate() {
      if (this.$refs.form.validate()) {
        this.createOrUpdate();
      } else {
        this.snack.show = true;
        this.snack.color = "warning";
        this.snack.message = "Atenção: Os campos obrigatórios precisam ser preenchidos!";
      }
    },
    createOrUpdate() {
      this.loader = true;
      const endpoint = this.id ? `/users/${this.id}` : "/users";
      const method = this.id ? "put" : "post";
      
      this.$store.dispatch("setLoader", true);
      this.$http[method](endpoint, this.data)
        .then((r) => {
          this.snack.show = true;
          this.snack.message = r.data.result;
          this.snack.color = "success";
          setTimeout(() => {
            this.$router.push("/users-manager");
          }, 1500);
        })
        .catch((e) => {
          this.snack.show = true;
          this.snack.message = e.message;
          this.snack.color = "error";
          this.loader = false;
          this.$store.dispatch("setLoader", false);
        });
    },
    uploadImage(event) {
      if (!event.target.files[0]) return;
      this.$store.dispatch("setLoader", true);
      const fd = new FormData(); fd.append("image", event.target.files[0], event.target.files[0].name);
      this.$http.post("/upload", fd).then((r) => {
        this.data.user_photo = r.data.cover; this.$store.dispatch("setLoader", false);
      }).catch((e) => {
        alert("Erro ao realizar upload da imagem tente novamente! " + e.message);
        this.$store.dispatch("setLoader", false);
      });
    },
  },
};
</script>

<style scoped>
.field-label {
  display: block;
  font-size: 9px;
  font-weight: 700;
  color: #94a3b8;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  margin-bottom: 4px;
}

.field-input >>> .v-input__control .v-input__slot {
  background-color: #f8fafc !important;
  border-radius: 6px !important;
}

.field-input >>> .v-input__control .v-input__slot fieldset {
  border-color: #e2e8f0 !important;
  border-width: 1px !important;
}

.field-input >>> .v-input__control .v-input__slot:hover fieldset {
  border-color: #f37021 !important;
}

.field-input >>> input,
.field-input >>> .v-select__selection {
  font-size: 13px !important;
  color: #334155 !important;
}

.custom-snack >>> .v-snack__wrapper {
  border-radius: 10px !important;
}

@keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }
.animate-spin { animation: spin 1s linear infinite; }
</style>